import { getJWTToken } from '../../../utils/auth';
import { authHeadersFor } from '../../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type FlagScope = 'all' | 'districts';

export type FlagState = {
  enabled: boolean;
  scope: FlagScope;
  districtIds: string[];
  updatedAt: string;
  updatedBy: string;
  note: string;
  revision: number;
};

export type FlagView = { key: string; hides: string; state: FlagState | null };

export type FlagGate = { kind: string; value?: string; note?: string };

export type FlagRelease = {
  id: string;
  name: string;
  summary: string;
  owner: string;
  stage: string;
  surfaces: string[];
  cards: number[];
  prs: number[];
  flags: FlagView[];
  gates: FlagGate[];
};

export type FlagDistrict = { id: string; name: string; state?: string };

export type FeatureFlagsList = {
  asOf: string;
  env: string;
  releases: FlagRelease[];
  unknownItems: FlagView[];
  districts: FlagDistrict[];
};

export type FlagChange = {
  enabled: boolean;
  scope: FlagScope;
  districtIds: string[];
  note: string;
  expectedRevision: number;
};

export class FlagRequestError extends Error {
  readonly status: number;

  readonly code: string;

  constructor(status: number, code: string) {
    super(`feature flags request failed: ${status} ${code}`.trim());
    this.name = 'FlagRequestError';
    this.status = status;
    this.code = code;
  }
}

export class FlagConflictError extends Error {
  readonly current: FlagState | null;

  constructor(current: FlagState | null) {
    super('The switch changed since it was loaded');
    this.name = 'FlagConflictError';
    this.current = current;
  }
}

type Raw = Record<string, unknown>;

const asRecord = (value: unknown): Raw | null => (typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Raw) : null);
const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asStrings = (value: unknown): string[] => (Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : []);
const asNumbers = (value: unknown): number[] => (Array.isArray(value) ? value.filter((v): v is number => typeof v === 'number') : []);
const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

export function parseState(raw: unknown): FlagState | null {
  const r = asRecord(raw);
  if (!r) return null;
  const scope: FlagScope = r.scope === 'districts' ? 'districts' : 'all';
  return {
    enabled: r.enabled === true,
    scope,
    districtIds: asStrings(r.districtIds),
    updatedAt: asString(r.updatedAt),
    updatedBy: asString(r.updatedBy),
    note: asString(r.note),
    revision: typeof r.revision === 'number' ? r.revision : 0,
  };
}

function parseFlag(raw: unknown): FlagView {
  const r = asRecord(raw) ?? {};
  return { key: asString(r.key), hides: asString(r.hides), state: parseState(r.state) };
}

function parseGate(raw: unknown): FlagGate {
  const r = asRecord(raw) ?? {};
  return { kind: asString(r.kind), value: asString(r.value) || undefined, note: asString(r.note) || undefined };
}

function parseRelease(raw: unknown): FlagRelease {
  const r = asRecord(raw) ?? {};
  return {
    id: asString(r.id),
    name: asString(r.name),
    summary: asString(r.summary),
    owner: asString(r.owner),
    stage: asString(r.stage),
    surfaces: asStrings(r.surfaces),
    cards: asNumbers(r.cards),
    prs: asNumbers(r.prs),
    flags: asArray(r.flags).map(parseFlag),
    gates: asArray(r.gates).map(parseGate),
  };
}

function parseDistrict(raw: unknown): FlagDistrict {
  const r = asRecord(raw) ?? {};
  return { id: asString(r.id), name: asString(r.name) || asString(r.id), state: asString(r.state) || undefined };
}

export function parseList(raw: unknown): FeatureFlagsList {
  const r = asRecord(raw) ?? {};
  return {
    asOf: asString(r.asOf),
    env: asString(r.env),
    releases: asArray(r.releases).map(parseRelease),
    unknownItems: asArray(r.unknownItems).map(parseFlag),
    districts: asArray(r.districts).map(parseDistrict),
  };
}

async function staffHeaders(): Promise<Record<string, string>> {
  return authHeadersFor(await getJWTToken(), null);
}

async function requestError(response: Response): Promise<FlagRequestError> {
  const payload = asRecord(await response.json().catch(() => null));
  return new FlagRequestError(response.status, asString(payload?.error));
}

export async function fetchFeatureFlags(): Promise<FeatureFlagsList> {
  const response = await fetch(`${apiUrl}/admin/feature-flags`, { headers: await staffHeaders() });
  if (!response.ok) throw await requestError(response);
  return parseList(await response.json());
}

export async function putFeatureFlag(key: string, change: FlagChange): Promise<FlagState> {
  const response = await fetch(`${apiUrl}/admin/feature-flags/${encodeURIComponent(key)}`, {
    method: 'PUT',
    headers: { ...(await staffHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(change),
  });
  if (response.status === 409) {
    const payload = asRecord(await response.json().catch(() => null));
    throw new FlagConflictError(parseState(payload?.current));
  }
  if (!response.ok) throw await requestError(response);
  const state = parseState(await response.json());
  if (!state) throw new FlagRequestError(response.status, 'invalid_state');
  return state;
}
